import {Dispatch} from '@reduxjs/toolkit'
import {getLogger} from '../services/logging'
import {fetchAuthSession} from 'aws-amplify/auth'
import {Hub} from 'aws-amplify/utils'
import {setUser} from '../store/slices/userSlice'
import {getConfig} from '../services/config'
import {Amplify} from 'aws-amplify'
import ReactGA from 'react-ga4'
import {JwtPayload} from 'jsonwebtoken'

const logger = getLogger('useInitCognitoAuth')

// amplify cognito configuration
const {cognitoUserPoolId, cognitoWebClientId} = getConfig()
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: cognitoUserPoolId,
      userPoolClientId: cognitoWebClientId
    }
  }
})

function attributesToUser(attributes: JwtPayload) {
  return {
    isAuthenticated: true,
    firstName: attributes.given_name,
    lastName: attributes.family_name,
    email: attributes.email,
    phone: attributes.phone_number
  }
}

/**
 * Initializes the Cognito authorization state.  Checks for a valid session token, etc.
 * @param dispatch The Redux dispatch function
 */
export async function initAuthState(dispatch: Function) {
  logger.info({message: 'Initializing auth state...'})
  try {
    const session = await fetchAuthSession()
    logger.info({message: 'User session...', data: {session}})

    if (session?.tokens?.idToken) {
      logger.info({message: 'User is already signed in'})
      const attributes = session.tokens.idToken.payload
      logger.info({message: 'User attributes...', data: {attributes}})
      dispatch(setUser(attributesToUser(attributes)))

      ReactGA.event({
        category: 'IBE',
        action: 'User_Session_Restored',
        value: 1
      })
    } else {
      logger.info({message: 'User is not signed in'})
      dispatch(setUser({isAuthenticated: false}))
    }
  } catch (e) {
    logger.info({message: 'User is not signed in'})
    dispatch(setUser({isAuthenticated: false}))
  }
}

/**
 * Initializes the Cognito authorization listener.  Listens for auth events and updates the Redux store.
 * @param dispatch The redux dispatch function
 * @returns
 */
export function initAuthListener(dispatch: Dispatch): Function {
  logger.info({message: 'Initializing auth listener...'})

  const handleSignIn = async (): Promise<void> => {
    const session = await fetchAuthSession()
    const attributes = session.tokens.idToken.payload
    const user = attributesToUser(attributes)

    logger.info({message: 'Signing in user...', data: {user}})
    dispatch(setUser(user))
  }

  const handleSignOut = (): void => {
    logger.info({message: 'Signing out user...'})
    dispatch(setUser({isAuthenticated: false}))
  }

  const authListener = async (data: any): Promise<void> => {
    logger.info({message: 'Auth event...', data})

    switch (data?.payload?.event) {
      case 'signedIn':
        logger.info({message: 'User signed in'})
        await handleSignIn()
        break
      case 'signedOut':
        logger.info({message: 'User signed out'})
        handleSignOut()
        break
      case 'tokenRefresh':
        logger.info({message: 'Auth token refresh succeeded'})
        break
      case 'tokenRefresh_failure':
        logger.error({message: 'Auth token refresh failed'})
        break
      default:
        logger.warn({
          message: 'Unhandled auth event',
          data: {event: data?.payload?.event}
        })
    }
  }

  // add auth listener function - Hub.listen returns an unsubscribe function in v6
  const unsubscribe = Hub.listen('auth', authListener)

  // return auth listener cleanup function
  return () => {
    unsubscribe()
  }
}
